import React, { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EnvService } from '@hub/services/core/env-service';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import {
  Button,
  Flex,
  Modal,
  ModalHandle,
  TextComponent,
} from '@monorepo/react-components';

interface FormActionsProps {
  isLoading: boolean;
  isDisabled: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

export const FormActions: React.FC<FormActionsProps> = ({
  isLoading,
  isDisabled,
  onCancel,
  onSubmit,
}) => {
  const withdrawMechanismEnabled = useGetFeatureFlag(
    'enable-revert-withdrawal-mechanism',
    false,
    {
      env: EnvService.application,
    },
  );

  const submitConfirmationModalRef = useRef<ModalHandle>(null);
  const { t } = useTranslation(['common', 'withdrawFunds']);

  const handleSubmit = () => {
    if (withdrawMechanismEnabled) {
      submitConfirmationModalRef.current?.open();
    } else {
      onSubmit();
    }
  };

  return (
    <Flex gap={39}>
      <Button type="link" disabled={isLoading} onClick={onCancel}>
        <TextComponent underline>{t('common:cancel')}</TextComponent>
      </Button>
      <Button
        type="primary"
        loading={isLoading}
        disabled={isDisabled}
        onClick={handleSubmit}
      >
        {t('common:submit')}
      </Button>
      <Modal
        ref={submitConfirmationModalRef}
        cancelButtonProps={{
          type: 'text',
        }}
        okText={t('withdrawFunds:confirmation_modal.confirm_button')}
        width={625}
        onOk={onSubmit}
      >
        <Flex
          vertical
          justify="center"
          align="center"
          gap={24}
          style={{ marginBottom: 32 }}
        >
          <TextComponent weight={600} level={2} color="var(--colorPrimary)">
            {t('withdrawFunds:confirmation_modal.title')}
          </TextComponent>
          <TextComponent level={5} textAlign="center">
            <Trans
              i18nKey="withdrawFunds:confirmation_modal.message"
              components={{ br: <br /> }}
            />
          </TextComponent>
          <TextComponent level={5} weight={600}>
            {t('withdrawFunds:confirmation_modal.question')}
          </TextComponent>
        </Flex>
      </Modal>
    </Flex>
  );
};
